import React, { useState, useEffect } from "react";
import { useUser } from '../../../../UserContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from "axios";
import { formatInTimeZone } from 'date-fns-tz'
import { parseISO, set, subDays } from 'date-fns'
import { processData2, NewUsersChart, AverageSessionDurationChart, ProductClicksChart, BounceRateChart } from "./Components/NewVisualizations";
import AverageRatingChart from "./Components/AverageRatingChart";
import OrdersPerWeekChart from "./Components/OrdersPerWeekChart";
import OrderStatusChart from "./Components/OrderStatusChart";
import FulfillmentTimeChart from "./Components/FulfillmentTimeChart";
import { ToastContainer } from 'react-toastify';
import { showToast } from '../../../../Constants/Toast';
import 'react-toastify/dist/ReactToastify.css';
import { orangeButton } from '../../../../Constants/Styles';
import { getVendorListings, getVendorOrders, getReviewsByVendor } from "../../../API_Handler";

const styles = {
    container: 'w-full flex flex-col overflow-y-auto mt-4',
    loadingOverlay: 'absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-50',
    loadingSpinner: 'animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500',
    header: 'text-xl md:text-3xl 2xl:text-3xl  pb-2 vendor-header',
    selectContainer: 'flex flex-row gap-2',
    select: 'h-full',
    flexWrapContainer: 'mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4',
    chartContainer: 'w-full h-[26rem] px-4 py-2 bg-white shadow-sm flex items-center justify-center',
    spinnerWrapper: 'flex items-center justify-center h-full w-full',
};

const Dashboard = () => {

    //User Context
    const { userListings, userOrders, userReviews, setUserListings, setUserOrders, setUserReviews } = useUser();

    const [_userOrders, _setUserOrders] = useState([]);
    const [_userReviews, _setUserReviews] = useState([]);
    const [_userListings, _setUserListings] = useState([]);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [ordersForSelectedProduct, setOrdersForSelectedProduct] = useState([]);
    const [reviewsForSelectedProduct, setReviewsForSelectedProduct] = useState([]);

    const [selectedTimeRange, setSelectedTimeRange] = useState('7');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [loading, setLoading] = useState(true);
    const pacificNow = formatInTimeZone(new Date(), 'America/Los_Angeles', 'yyyy-MM-dd');


    // This is what happens on initial load, retrieves all users Listings, Orders, and Reviews
    useEffect(() => {
        const fetchListings = async () => {
            if (userListings) {
                _setUserListings(userListings);
                return;
            }
            // try {
            //     const products = await getVendorListings();
            //     _setUserListings(products);
            //     setUserListings(products);
            // } catch (error) {
            //     console.error("Error fetching vendor listings:", error);
            //     showToast('There seems to be an error. Please try again later.', 'error');
            // }
        }

        const fetchOrders = async () => {
            if (userOrders) {
                _setUserOrders(userOrders);
                return;
            }
            // try {
            //     const orders = await getVendorOrders();
            //     _setUserOrders(orders);
            //     setUserOrders(orders);
            // } catch (error) {
            //     console.error("Error fetching vendor orders:", error);
            //     showToast('There seems to be an error. Please try again later.', 'error');
            // }
        }

        const fetchReviews = async () => {
            if (userReviews) {
                _setUserReviews(userReviews);
                return;
            }

            // try {
            //     const reviews = await getReviewsByVendor();
            //     _setUserReviews(reviews);
            //     setUserReviews(reviews);
            // } catch (error) {
            //     console.error("Error fetching vendor reviews:", error);
            //     showToast('There seems to be an error. Please try again later.', 'error');
            // }
        }

        const initializeData = async () => {
            fetchListings();
            fetchOrders();
            fetchReviews();

            setSelectedTimeRange('7');
            setStartDate(formatInTimeZone(subDays(new Date(), parseInt(7, 10) - 1), 'America/Los_Angeles', 'yyyy-MM-dd'));
            setEndDate(pacificNow);
        }
        initializeData();
    }, []);

    useEffect(() => {
        if (selectedTimeRange != "custom") {
            if (selectedTimeRange == "All Time") {
                if (selectedProduct) {
                    const createdAtDate = new Date(selectedProduct.created_at);
                    const formattedDate = createdAtDate.toLocaleDateString('en-CA');
                    setStartDate(formattedDate);
                }
                else {
                    setStartDate(pacificNow);
                }
            }
            else if (selectedTimeRange != "All Time") {
                setStartDate(formatInTimeZone(subDays(new Date(), parseInt(selectedTimeRange, 10) - 1), 'America/Los_Angeles', 'yyyy-MM-dd'));
                setEndDate(pacificNow);
            }
            if (selectedProduct) {
                retrieveAnalyticsMetrics(selectedProduct.id, startDate, endDate);
            }
        }
    }, [selectedTimeRange]);

    useEffect(() => {
        if (_userListings.length > 0) {
            setSelectedProduct(_userListings[0]);
        }
    }, [_userListings]);

    useEffect(() => {
        setSelectedTimeRange('7');
        if (selectedProduct) {
            const filteredOrders = _userOrders.filter(order => order.product.id === selectedProduct.id);
            setOrdersForSelectedProduct(filteredOrders);
            const filteredReviews = _userReviews.filter(review => review.product.id === selectedProduct.id);
            setReviewsForSelectedProduct(filteredReviews);
            retrieveAnalyticsMetrics(selectedProduct.id, startDate, endDate);
        }
    }, [selectedProduct]);

    const [timeSeriesNewUsers, setTimeSeriesNewUsers] = useState(null);
    const [timeSeriesEventCount, setTimeSeriesEventCount] = useState(null);
    const [timeSeriesAverageEngagementTime, setTimeSeriesAverageEngagementTime] = useState(null);
    const [timeSeriesBounceRate, setTimeSeriesBounceRate] = useState(null);

    const retrieveAnalyticsMetrics = async (productId, retrieveFrom, retrieveTo) => {
        const daysDifference = Math.ceil((new Date(retrieveTo) - new Date(retrieveFrom)) / (1000 * 60 * 60 * 24));

        setLoading(true);
        setTimeSeriesNewUsers(null);
        setTimeSeriesEventCount(null);
        setTimeSeriesAverageEngagementTime(null);
        setTimeSeriesBounceRate(null);

        const apiEndpoint = 'https://xjmn0g1tf5.execute-api.us-east-1.amazonaws.com/production/get-analytics-data';

        const params = {
            product_id: productId,
            startDate: retrieveFrom,
            endDate: retrieveTo
        };

        try {
            const { idToken } = (await fetchAuthSession()).tokens ?? {};

            if (!idToken) {
                throw new Error('No authenticated user session found');
            }

            const response = await axios.get(apiEndpoint, {
                params,
                headers: {
                    'Authorization': `Bearer ${idToken.toString()}`
                }
            });

            if (response.data) {
                const fullDataArray = createFullDataArray(response.data, retrieveFrom, daysDifference + 1);
                setTimeSeriesNewUsers(processData2(fullDataArray, "pageViews.newUsers", "New Users"));
                setTimeSeriesAverageEngagementTime(processData2(fullDataArray, "pageViews.averageSessionDuration", "Average Session Duration"));
                setTimeSeriesEventCount(processData2(fullDataArray, "productClicks.productClicks", "Product Clicks"));
                setTimeSeriesBounceRate(processData2(fullDataArray, "pageViews.bounceRate", "Bounce Rate"));
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);
            const emptyData = await generateEmptyData(daysDifference + 1, retrieveFrom, retrieveTo);
            setTimeSeriesNewUsers(processData2(emptyData, "pageViews.newUsers", "New Users"));
            setTimeSeriesAverageEngagementTime(processData2(emptyData, "pageViews.averageSessionDuration", "Average Session Duration"));
            setTimeSeriesEventCount(processData2(emptyData, "productClicks.productClicks", "Product Clicks"));
            setTimeSeriesBounceRate(processData2(emptyData, "pageViews.bounceRate", "Bounce Rate"));
            console.error('Error fetching analytics metrics:', error);
        }
    };

    const createFullDataArray = (data, startDate, totalDays) => {
        const fullDataArray = [];
        const start = new Date(startDate);

        for (let i = 0; i < totalDays; i++) {
            const currentDate = new Date(start);
            currentDate.setDate(start.getDate() + i);
            const dateString = currentDate.toISOString().split('T')[0];

            const existingPageViews = data.find(item => item.pageViews && item.pageViews.date === dateString);
            const existingProductClicks = data.find(item => item.productClicks && item.productClicks.date === dateString);

            fullDataArray.push({
                pageViews: existingPageViews ? existingPageViews.pageViews : {
                    date: dateString,
                    newUsers: "0",
                    averageSessionDuration: "0",
                    bounceRate: "0",
                    screenPageViews: "0"
                },
                productClicks: existingProductClicks ? existingProductClicks.productClicks : {
                    date: dateString,
                    productClicks: "0"
                }
            });
        }

        return fullDataArray;
    };

    const generateEmptyData = (numDays, retrieveFrom, retrieveTo) => {
        const emptyData = [];
        const startDate = new Date(retrieveFrom);

        for (let i = 0; i < numDays; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);

            emptyData.push({
                pageViews: {
                    date: currentDate.toISOString().split('T')[0],
                    newUsers: "0",
                    averageSessionDuration: "0",
                    bounceRate: "0",
                    screenPageViews: "0"
                },
                productClicks: {
                    productClicks: "0",
                    date: currentDate.toISOString().split('T')[0],
                },
            });
        }

        return emptyData;
    };

    const filterDataByDate = async (startDate, endDate) => {
        const myOrders = _userOrders.filter(order => order.product.id === selectedProduct.id);
        const myReviews = _userReviews.filter(review => review.product.id === selectedProduct.id);

        const startDateObj = parseISO(startDate);
        const endDateObj = parseISO(endDate);

        const filteredOrders = myOrders.filter(order => {
            const orderDate = parseISO(order.created_at);
            return !isNaN(orderDate) && orderDate >= startDateObj && orderDate <= endDateObj;
        });
        setOrdersForSelectedProduct(filteredOrders);

        const filteredReviews = myReviews.filter(review => {
            const reviewDate = parseISO(review.created_at);
            return !isNaN(reviewDate) && reviewDate >= startDateObj && reviewDate <= endDateObj;
        });
        setReviewsForSelectedProduct(filteredReviews);
    };

    const handleStartDateChange = (e) => {
        const selectedDate = e.target.value;
        const minDate = new Date('2015-08-14');
        const endDateObj = endDate ? endDate : pacificNow;

        if (selectedDate < minDate) {
            showToast('Start date cannot be before 2015-08-14. Please select a different date.', 'error');
            setStartDate('2015-08-14');
        } else if (selectedDate >= endDateObj) {
            showToast('Start date cannot be the same as orafter the end date. Please select a different date.', 'error');
            const newStartDate = new Date(endDateObj);
            newStartDate.setDate(newStartDate.getDate() - 1);
            setStartDate(newStartDate.toISOString().split('T')[0]);
        } else {
            setStartDate(e.target.value);
        }
    };

    const handleEndDateChange = (e) => {
        const selectedDate = e.target.value;
        const startDateObj = startDate ? startDate : pacificNow;

        if (selectedDate > pacificNow) {
            showToast('End date cannot be in the future. Please select a different date.', 'error');
            setEndDate(pacificNow);
        } else if (startDateObj && selectedDate < startDateObj) {
            showToast('End date cannot be before the start date. Please select a different date.', 'error');
            setEndDate(startDate);
        } else {
            setEndDate(e.target.value);
        }
    };

    const handleCustomDateChange = async () => {
        if (!startDate || !endDate) {
            showToast('Please select both start and end dates', 'error');
            return;
        }

        if (endDate > pacificNow) {
            showToast('End date cannot be in the future', 'error');
            return;
        }

        if (selectedProduct) {
            retrieveAnalyticsMetrics(selectedProduct.id, startDate, endDate);
            filterDataByDate(startDate, endDate);
        }
    };

    return (
        <div className={styles.container}>
            <ToastContainer />
            <div className="flex flex-col">
                <div className="flex flex-row justify-between items-center">
                    <div className="flex flex-row gap-2 h-11">
                        <h3 className="text-lg md:text-xl 2xl:text-2xl h-full items-center flex">Select Product:</h3>
                        <select
                            className="border border-gray-300 h-full p-2 rounded"
                            onChange={(e) => {
                                const selected = _userListings.find(product => product.id === e.target.value);
                                setSelectedProduct(selected);
                            }}
                            value={selectedProduct?.id || ''}
                        >
                            {_userListings.map(product => (
                                <option key={product?.id} value={product?.id}>
                                    {product.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-row gap-2 h-11">
                        <h3 className="text-lg md:text-xl 2xl:text-2xl h-full items-center flex">For:</h3>
                        <select
                            className="border border-gray-300 h-full p-2 rounded"
                            value={selectedTimeRange}
                            onChange={(e) => {
                                setSelectedTimeRange(e.target.value);
                                if (e.target.value != 'custom' && e.target.value != 'All Time') {
                                    const daysToSubtract = parseInt(e.target.value, 10) - 1;
                                    const start = formatInTimeZone(subDays(new Date(), daysToSubtract), 'America/Los_Angeles', 'yyyy-MM-dd');
                                    setStartDate(start);
                                }
                            }}
                        >
                            <option value="All Time">All Time</option>
                            <option value="7">Past 7 Days</option>
                            <option value="30">Past 30 Days</option>
                            <option value="90">Past 90 Days</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>

                    {selectedTimeRange === 'custom' && (
                        <div className="flex flex-col gap-4 sm:flex-row sm:gap-2">
                            <div className="flex flex-row gap-2">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                />
                                <input
                                    type="date"
                                    className="form-control"
                                    value={endDate}
                                    onChange={handleEndDateChange} />
                            </div>
                            <button
                                className={orangeButton}
                                onClick={handleCustomDateChange}
                            >
                                Apply
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.flexWrapContainer}>
                <div className={styles.chartContainer}>
                    {loading ? (
                        <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                    )
                        :
                        timeSeriesNewUsers ? (
                            <NewUsersChart chartData={timeSeriesNewUsers} />
                        ) : (
                            <h3>There is no data available for this time period.</h3>
                        )}
                </div>

                <div className={styles.chartContainer}>
                    {loading ? (
                        <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                    )
                        :
                        timeSeriesAverageEngagementTime ? (
                            <AverageSessionDurationChart chartData={timeSeriesAverageEngagementTime} />
                        ) : (
                            <h3>There is no data available for this time period.</h3>

                        )}
                </div>

                <div className={styles.chartContainer}>
                    {loading ? (
                        <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                    )
                        :
                        timeSeriesEventCount ? (
                            <ProductClicksChart chartData={timeSeriesEventCount} />
                        ) : (
                            <h3>There is no data available for this time period.</h3>
                        )}
                </div>

                <div className={styles.chartContainer}>
                    {loading ? (
                        <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                    )
                        :
                        timeSeriesBounceRate ? (
                            <BounceRateChart chartData={timeSeriesBounceRate} />
                        ) : (
                            <h3>There is no data available for this time period.</h3>
                        )}
                </div>


                <div className={styles.chartContainer}>
                    {reviewsForSelectedProduct.length > 0 ? (
                        <AverageRatingChart data={reviewsForSelectedProduct} />
                    ) : (
                        <h3 className="text-xl md:text-2xl">There are no reviews for this time period</h3>
                    )}
                </div>

                <div className={styles.chartContainer}>
                    {ordersForSelectedProduct.length > 0 ? (
                        <OrdersPerWeekChart data={ordersForSelectedProduct} startDate={startDate} endDate={endDate} />
                    ) : (
                        <h3 className="text-xl md:text-2xl">There are no orders for this time period</h3>
                    )}
                </div>

                <div className={styles.chartContainer}>
                    {ordersForSelectedProduct.length > 0 ? (
                        <OrderStatusChart data={ordersForSelectedProduct} />
                    ) : (
                        <h3 className="text-xl md:text-2xl">There are no orders for this time period</h3>
                    )}
                </div>

                <div className={styles.chartContainer}>
                    {ordersForSelectedProduct.length > 0 ? (
                        <FulfillmentTimeChart data={ordersForSelectedProduct} />
                    ) : (
                        <h3 className="text-xl md:text-2xl">There are no fulfillment times for this time period</h3>
                    )}
                </div>

            </div>

        </div>
    );
}

export default Dashboard;